import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import ChatMessageRenderer from './ChatMessageRenderer';

const API_URL = process.env.REACT_APP_API_URL || '';

const D = {
  bg:           '#2f2f2f',
  surface:      '#252323',
  surfaceAlt:   '#201f1f',
  border:       '#2a2a2a',
  borderStrong: '#3a3a3a',
  borderGold:   '#bca97a',
  gold:         '#e2bf6d',
  goldDim:      '#7a5c1a',
  goldSoft:     '#1c1608',
  text:         '#ffffff',
  textMuted:    '#a0a0a0',
  textDim:      '#555555',
  userBubble:   '#141414',
  userBorder:   '#1f1609',
  botBubble:    '#111111',
  botBorder:    '#2a2a2a',
};

const UI = "'Trebuchet MS', 'Gill Sans', system-ui, sans-serif";

const GLOBAL_CSS = `
  *, *::before, *::after { box-sizing: border-box !important; }
  html, body, #root {
    margin: 0 !important; padding: 0 !important;
    width: 100% !important; height: 100% !important;
    overflow: hidden !important; background: #2f2f2f !important;
  }
  button:focus, button:focus-visible,
  input:focus,  input:focus-visible { outline: none !important; box-shadow: none !important; }
  @keyframes cmrBounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-5px)} }
`;

const ChatInterface = forwardRef(function ChatInterface(
  { token, userId, casinoId, onLogout, onMinimize, onToggleSidebar, sidebarOpen, activeConversationId, onConversationCreated },
  ref
) {
  const [messages,     setMessages]    = useState([]);
  const [inputValue,   setInputValue]  = useState('');
  const [loading,      setLoading]     = useState(false);
  const [inputFocused, setInputFocused]= useState(false);
  const [showGlossary, setShowGlossary]= useState(false);
  const [visualMode,   setVisualMode]  = useState(false);   // Text (default) · Visual toggle
  const messagesEndRef = useRef(null);
  const API_BASE_URL   = process.env.REACT_APP_API_URL || '';

  useImperativeHandle(ref, () => ({
    loadConversation: async (conversationId) => {
      try {
        const res = await fetch(`${API_BASE_URL}/conversations/${conversationId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMessages(data.messages.map(msg => ({
            role:        msg.role,
            content:     msg.content,
            timestamp:   new Date(msg.created_at),
            fromHistory: true,
            chart_spec:  (() => {
              try { return msg.chart_spec ? (typeof msg.chart_spec === 'string' ? JSON.parse(msg.chart_spec) : msg.chart_spec) : null; }
              catch { return null; }
            })(),
          })));
        }
      } catch (err) { console.error('Failed to load conversation:', err); }
    },
    clearMessages: () => setMessages([]),
  }));

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const clearConversation = async () => {
    try { await axios.delete(`${API_URL}/conversation/clear`, { headers: { Authorization: `Bearer ${token}` } }); }
    catch {}
    setMessages([]);
  };

  const sendMessage = async (question) => {
    if (!question.trim()) return;
    setLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: question, timestamp: new Date() }]);
    setInputValue('');
    try {
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const { data } = await axios.post(
        `${API_URL}/chat`,
        { question, conversation_history: history, conversation_id: activeConversationId || null, visual_mode: visualMode },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      if (data.conversation_id && !activeConversationId && onConversationCreated) {
        onConversationCreated(data.conversation_id);
      }
      setMessages(prev => [...prev, {
        role: 'assistant', content: data.insights,
        data: data.data, casino_id: data.casino_id,
        chart_spec: data.chart_spec || null,
        query_id: data.query_id || null,
        timestamp: new Date(),
      }]);
    } catch (error) {
      let msg = 'An error occurred. Please try again.';
      if (error.code === 'ERR_NETWORK')        msg = `Cannot connect to backend at ${API_URL}.`;
      else if (error.response?.status === 401) { msg = 'Session expired. Please log in again.'; setTimeout(onLogout, 2000); }
      else if (error.response?.data?.detail)   msg = error.response.data.detail;
      setMessages(prev => [...prev, { role: 'assistant', content: msg, timestamp: new Date() }]);
    } finally { setLoading(false); }
  };

  const handleSubmit = e => { e.preventDefault(); sendMessage(inputValue); };

  const sendFeedback = async (queryId, rating, question, answer, comment) => {
    try {
      await axios.post(
        `${API_URL}/feedback`,
        { query_id: queryId, rating, question, answer, comment: comment || null },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      );
      return true;
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      return false;
    }
  };

  const CHIPS = [
    'Deposits last 30 days',
    'Top 10 games by revenue',
    'Which KPIs are underperforming?',
    'Show risk overview',
  ];

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

      <div style={{
        position:      'relative',
        width:         '100%',
        height:        '100vh',
        display:       'flex',
        flexDirection: 'column',
        background:    D.bg,
        overflow:      'hidden',
        fontFamily:    UI,
      }}>

        {/* ── HEADER ── */}
        <header style={{
          flexShrink:     0,
          height:         46,
          background:     D.surface,
          borderBottom:   `1px solid ${D.border}`,
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          padding:        '0 14px',
          gap:            10,
        }}>

          {/* Left: sidebar toggle + logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>

            {/* Sidebar toggle */}
            <button
              onClick={onToggleSidebar}
              title={sidebarOpen ? 'Hide history' : 'Show history'}
              style={{
                width:          30,
                height:         30,
                background:     'transparent',
                border:         `1px solid ${D.borderStrong}`,
                borderRadius:   6,
                color:          sidebarOpen ? D.gold : D.textMuted,
                cursor:         'pointer',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                fontSize:       14,
                fontWeight:     700,
                flexShrink:     0,
                transition:     'all 0.12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = D.borderGold; e.currentTarget.style.color = D.gold; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = D.borderStrong; e.currentTarget.style.color = sidebarOpen ? D.gold : D.textMuted; }}
            >☰</button>

            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontSize: 16 }}>🎰</span>
              <span style={{
                fontSize: 11, fontWeight: 700,
                color: D.gold, letterSpacing: '0.12em',
                textTransform: 'uppercase', fontFamily: UI,
                whiteSpace: 'nowrap',
              }}>Casino Analytics</span>
            </div>
          </div>

          {/* Right: user + actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>

            {/* User label */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 5,
              fontSize: 11, color: D.textMuted, fontFamily: UI,
            }}>
              <span>👤</span>
              <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {userId}
              </span>
            </div>

            {/* Divider */}
            <div style={{ width: 1, height: 16, background: D.borderStrong }} />

            {/* Text · Visual segmented toggle */}
            <FormatToggle visualMode={visualMode} onChange={setVisualMode} />

            {/* Glossary */}
            <HeaderBtn onClick={() => setShowGlossary(true)} title="Metric definitions & help">
              Glossary
            </HeaderBtn>

            {/* Clear */}
            <HeaderBtn onClick={clearConversation} title="Clear chat">
              Clear
            </HeaderBtn>

            {/* Minimise */}
            {onMinimize && (
              <HeaderBtn onClick={onMinimize} title="Minimise to pill">
                −
              </HeaderBtn>
            )}

            {/* Sign out */}
            <HeaderBtn onClick={onLogout} title="Sign out" danger>
              Sign out
            </HeaderBtn>
          </div>
        </header>

        {/* ── MESSAGES ── */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '20px 18px' }}>
          <div style={{ maxWidth: 860, margin: '0 auto', width: '100%' }}>

            {messages.length === 0 ? (
              <div style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                minHeight: '55vh', textAlign: 'center', gap: 14,
              }}>
                <div style={{ fontSize: 48 }}>🎰</div>
                <h2 style={{ color: D.text, fontSize: 22, fontWeight: 600, fontFamily: 'Georgia, serif', margin: 0 }}>
                  Ask me about your casino data
                </h2>
                <p style={{ color: D.textMuted, fontSize: 13, fontFamily: UI, margin: 0, lineHeight: 1.6 }}>
                  Deposits, revenue, players, risk — just ask
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 8, maxWidth: 560 }}>
                  {CHIPS.map((q, i) => (
                    <button key={i} onClick={() => sendMessage(q)} style={{
                      background: D.surfaceAlt, border: `1px solid ${D.border}`,
                      borderRadius: 20, color: D.textMuted, cursor: 'pointer',
                      fontSize: 12, padding: '7px 16px', fontFamily: UI, transition: 'all 0.15s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = D.gold; e.currentTarget.style.color = D.gold; e.currentTarget.style.background = D.goldSoft; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = D.border; e.currentTarget.style.color = D.textMuted; e.currentTarget.style.background = D.surfaceAlt; }}
                    >{q}</button>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, width: '100%' }}>
                {messages.map((msg, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    width: '100%',
                  }}>
                    <div style={{
                      maxWidth:     msg.role === 'user' ? '60%' : '92%',
                      borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '4px 16px 16px 16px',
                      padding:      '11px 15px',
                      background:   msg.role === 'user' ? D.userBubble : D.botBubble,
                      border:       `1px solid ${msg.role === 'user' ? D.userBorder : D.botBorder}`,
                      wordBreak:    'break-word',
                    }}>
                      {msg.role === 'assistant'
                        ? <ChatMessageRenderer
                            text={msg.content}
                            chartSpec={msg.chart_spec}
                            queryId={msg.query_id}
                            question={idx > 0 ? messages[idx - 1].content : ''}
                            onFeedback={sendFeedback}
                          />
                        : <div style={{ fontSize: 13, color: D.text, lineHeight: 1.65, fontFamily: 'Georgia, serif', whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                      }
                      <div style={{
                        fontSize: 10, color: D.textDim, marginTop: 5,
                        fontFamily: UI, textAlign: msg.role === 'user' ? 'right' : 'left',
                      }}>
                        {msg.timestamp instanceof Date ? msg.timestamp.toLocaleTimeString() : new Date(msg.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}

                {loading && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <div style={{
                      background: D.botBubble, border: `1px solid ${D.botBorder}`,
                      borderRadius: '4px 16px 16px 16px', padding: '12px 16px',
                      display: 'flex', gap: 5, alignItems: 'center',
                    }}>
                      {[0, 0.15, 0.3].map((delay, i) => (
                        <div key={i} style={{
                          width: 7, height: 7, borderRadius: '50%', background: D.goldDim,
                          animation: 'cmrBounce 0.9s infinite', animationDelay: `${delay}s`,
                        }} />
                      ))}
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* ── INPUT BAR ── */}
        <div style={{
          flexShrink: 0,
          background: D.surface,
          borderTop:  `1px solid ${D.border}`,
          padding:    '12px 18px',
        }}>
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                placeholder="Ask a question about your casino data..."
                style={{
                  flex:         1,
                  padding:      '11px 16px',
                  background:   D.surfaceAlt,
                  border:       `1px solid ${inputFocused ? D.borderGold : D.border}`,
                  borderRadius: 10,
                  color:        D.text,
                  fontSize:     13,
                  outline:      'none',
                  fontFamily:   'Georgia, serif',
                  transition:   'border-color 0.15s',
                }}
              />
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding:      '11px 22px',
                  background:   D.goldSoft,
                  border:       `1px solid ${D.borderGold}`,
                  borderRadius: 10,
                  color:        D.gold,
                  cursor:       loading ? 'not-allowed' : 'pointer',
                  fontSize:     13,
                  fontWeight:   600,
                  fontFamily:   UI,
                  opacity:      loading ? 0.5 : 1,
                  whiteSpace:   'nowrap',
                  transition:   'all 0.15s',
                }}
              >
                {loading ? '...' : 'Send →'}
              </button>
            </form>
            <p style={{
              fontSize: 10, color: D.textDim, fontFamily: UI,
              margin: '7px 0 0', textAlign: 'center', opacity: 0.6,
            }}>
              © Casino Data Analytics — Powered by Bambi AI
            </p>
          </div>
        </div>
      </div>

      <GlossaryPanel
        isOpen={showGlossary}
        onClose={() => setShowGlossary(false)}
        apiUrl={API_URL}
        token={token}
      />
    </>
  );
});

/* ── Text · Visual segmented toggle ── */
function FormatToggle({ visualMode, onChange }) {
  const segStyle = active => ({
    display:        'flex',
    alignItems:     'center',
    gap:            5,
    height:         26,
    padding:        '0 12px',
    borderRadius:   5,
    fontSize:       11,
    fontWeight:     600,
    fontFamily:     "'Trebuchet MS', system-ui, sans-serif",
    cursor:         'pointer',
    whiteSpace:     'nowrap',
    background:     active ? D.goldSoft : 'transparent',
    color:          active ? D.gold : '#888',
    border:         `1px solid ${active ? D.borderGold : 'transparent'}`,
    transition:     'all 0.12s',
  });
  return (
    <div
      title="Default reply format — Text or Visual"
      style={{
        display:      'flex',
        alignItems:   'center',
        gap:          2,
        padding:      2,
        background:   '#1a1a1a',
        border:       `1px solid ${D.borderStrong}`,
        borderRadius: 7,
      }}
    >
      <button type="button" onClick={() => onChange(false)} style={segStyle(!visualMode)}>
        📝 Text
      </button>
      <button type="button" onClick={() => onChange(true)} style={segStyle(visualMode)}>
        📊 Visual
      </button>
    </div>
  );
}

/* ── Small reusable header button ── */
function HeaderBtn({ onClick, title, danger, children }) {
  const base = {
    height:         28,
    padding:        '0 10px',
    background:     danger ? 'rgba(248,81,73,0.08)' : 'transparent',
    border:         `1px solid ${danger ? '#5a2020' : '#3a3a3a'}`,
    borderRadius:   6,
    color:          danger ? '#c06060' : '#888',
    cursor:         'pointer',
    fontSize:       11,
    fontWeight:     500,
    fontFamily:     "'Trebuchet MS', system-ui, sans-serif",
    whiteSpace:     'nowrap',
    display:        'flex',
    alignItems:     'center',
    transition:     'all 0.12s',
  };
  return (
    <button
      onClick={onClick}
      title={title}
      style={base}
      onMouseEnter={e => {
        e.currentTarget.style.background = danger ? 'rgba(248,81,73,0.2)' : '#2a2a2a';
        e.currentTarget.style.color      = danger ? '#f85149' : '#ccc';
        e.currentTarget.style.borderColor= danger ? '#7a2020' : '#555';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background  = danger ? 'rgba(248,81,73,0.08)' : 'transparent';
        e.currentTarget.style.color       = danger ? '#c06060' : '#888';
        e.currentTarget.style.borderColor = danger ? '#5a2020' : '#3a3a3a';
      }}
    >
      {children}
    </button>
  );
}

/* ── Glossary panel (metric definitions & help) ────────────────────────── */
function GlossaryPanel({ isOpen, onClose, apiUrl, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || data) return;  // fetch once per mount, not on every open
    setLoading(true);
    setError(null);
    axios.get(`${apiUrl}/glossary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load glossary'))
      .finally(() => setLoading(false));
  }, [isOpen, data, apiUrl, token]);

  if (!isOpen) return null;

  const rowStyle = { marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${D.border}` };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '90%', maxWidth: 640, maxHeight: '82vh', overflowY: 'auto',
          background: D.surface, border: `1px solid ${D.borderStrong}`,
          borderRadius: 12, padding: 20, fontFamily: UI,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: D.gold, letterSpacing: '0.04em' }}>
            📖 Metric Glossary
          </span>
          <button onClick={onClose} style={{
            background: 'transparent', border: `1px solid ${D.borderStrong}`,
            borderRadius: 6, width: 26, height: 26, color: D.textMuted, cursor: 'pointer', fontSize: 13,
          }}>✕</button>
        </div>

        {loading && <p style={{ color: D.textMuted, fontSize: 12 }}>Loading…</p>}
        {error && <p style={{ color: '#c06060', fontSize: 12 }}>{error}</p>}

        {data && data.dimensions?.length > 0 && (
          <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${D.borderStrong}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: D.gold, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              Available Dimensions
            </div>
            <div style={{ fontSize: 11, color: D.textDim, marginBottom: 10 }}>
              Any metric below can be grouped or filtered by these — e.g. "deposits by country", "NGR by segment".
            </div>
            {data.dimensions.map((d, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <span style={{
                  fontSize: 11, fontWeight: 600, color: D.gold, background: D.goldSoft,
                  border: `1px solid ${D.borderGold}`, borderRadius: 5, padding: '2px 8px',
                  whiteSpace: 'nowrap', height: 'fit-content',
                }}>{d.dimension}</span>
                <span style={{ fontSize: 12, color: D.textMuted, lineHeight: 1.5 }}>{d.definition}</span>
              </div>
            ))}
          </div>
        )}

        {data && Object.entries(data.categories || {}).map(([category, items]) => (
          <div key={category} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: D.gold, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              {category}
            </div>
            {items.map((item, i) => (
              <div key={i} style={rowStyle}>
                <div style={{ fontSize: 13, fontWeight: 600, color: D.text }}>{item.metric}</div>
                <div style={{ fontSize: 12, color: D.textMuted, margin: '3px 0' }}>{item.definition}</div>
                {item.formula && (
                  <div style={{ fontSize: 11, color: D.gold, fontFamily: 'monospace', margin: '3px 0' }}>
                    {item.formula}
                  </div>
                )}
                {item.breakdowns?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center', marginTop: 4 }}>
                    <span style={{ fontSize: 10, color: D.textDim, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Dimensions:
                    </span>
                    {item.breakdowns.map((b, bi) => (
                      <span key={bi} style={{
                        fontSize: 10, color: D.textMuted, background: D.surfaceAlt,
                        border: `1px solid ${D.border}`, borderRadius: 4, padding: '1px 6px',
                      }}>{b}</span>
                    ))}
                  </div>
                )}
                {item.example && (
                  <div style={{ fontSize: 11, color: D.textDim, fontStyle: 'italic', marginTop: 2 }}>
                    e.g. "{item.example}"
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}

        {data && data.pending_definition?.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#c9a45c', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              Coming soon — pending definition
            </div>
            {data.pending_definition.map((item, i) => (
              <div key={i} style={rowStyle}>
                <div style={{ fontSize: 13, fontWeight: 600, color: D.text }}>{item.metric}</div>
                <div style={{ fontSize: 12, color: D.textMuted, margin: '3px 0' }}>{item.definition}</div>
              </div>
            ))}
          </div>
        )}

        {data && data.not_available?.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: D.textDim, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              Not currently available
            </div>
            {data.not_available.map((item, i) => (
              <div key={i} style={rowStyle}>
                <div style={{ fontSize: 13, fontWeight: 600, color: D.text }}>{item.metric}</div>
                <div style={{ fontSize: 12, color: D.textMuted, margin: '3px 0' }}>{item.definition}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatInterface;